import qs, {parse, stringify} from 'qs'
import {
  anyPass,
  concat,
  flip,
  isEmpty,
  isNil,
  join,
  map,
  pipe,
  reject
} from 'ramda'

/** @hidden */
type UrlsType = {
  root: string
}

/** @hidden */
const urls: UrlsType = {
  root: '/'
}

export default urls

/**
 *
 * Removes specified parameter from provided URL
 * @returns A new URL without the specified parameter
 * @param url the url from which the parameter will be removed
 * @param parameter the chosen parameter to be removed
 */

/**
 * Removes a specific parameter from a URL
 * @param url - The URL from which the parameter will be removed
 * @param parameter - The chosen parameter to be removed
 * @returns URL string with the parameter removed
 */
export const removeURLParameter = (url: string, parameter: string): string => {
  try {
    const urlObj = new URL(url)

    urlObj.searchParams.delete(parameter)

    return urlObj.toString()
  } catch (e) {
    return url
  }
}

/** @hidden */
const cleanArrayFromFalsies = reject(anyPass([isEmpty, isNil]))

/**
 *
 * Trims all kinds of slashes from the URL
 * @returns A new URL without slashes
 * @param url The url to be trimmed of slashes
 */
export const trimSlashes = (url: string): string =>
  url ? url.replace(/^\/|\/+$/g, '') : ''

/**
 *
 * Ensures that the URL has correct slashes, if not, replaces it
 * @returns A URL which will have the correct slashes
 * @param url the url with correct slashes
 */
export const ensureUrlCorrectSlashes = pipe(
  map(trimSlashes),
  cleanArrayFromFalsies,
  join('/'),
  flip(concat)('/')
)

/**
 * Gets the value of a URL param
 * @param __namedParameters - Object containing search and param
 * @param __namedParameters.search - The URL from which you want to get a value
 * @param __namedParameters.param - The param/key of which you want to get the value
 * @returns The value of the specified param
 */
export const getUrlParam = ({
  search,
  param
}: {
  search: string
  param: string
}) => {
  const searchObject = parseQueryString(search)
  return searchObject[param]
}

/**
 * Checks if a URL has a specific parameter
 * @param __namedParameters - Object containing search and param
 * @param __namedParameters.search - The URL from which you want to check
 * @param __namedParameters.param - The param/key to be searched for
 * @returns True if the parameter exists in the URL
 */
export const urlHasParam = ({
  search,
  param
}: {
  search: string
  param: string
}) => {
  return Boolean(getUrlParam({search, param}))
}

/**
 * Trim leading `?` and parse
 * @param queryString URL query string. Might contain a leading `?`.
 * @returns An object of parsed query parameters
 */
export const parseQueryString = <T = ReturnType<typeof parse>>(
  queryString: string,
  removeDuplicates = false
): T =>
  parse(queryString, {
    ignoreQueryPrefix: true,
    duplicates: removeDuplicates ? 'first' : 'combine'
  }) as T

// Covers the multi-part public suffixes our brands run on (co.il, com.tr, co.uk, com.br...)
// without shipping the full Public Suffix List (tldts). If a partner lands on a suffix this
// pattern misses, switch to tldts instead of extending it.
const secondLevelPublicSuffixPattern =
  /\.(?:co|com|net|org|gov|edu|ac)\.[a-z]{2}$/i

/**
 * Return the registrable domain from the given host name, without subdomains.
 * Used mainly as the cookie `domain`, so it must never return a public suffix
 * (e.g. `co.il`) or browsers will silently drop the cookie.
 * @param hostName - secure.findhotel.net (as returned by window.location.hostname)
 */
export const getDomainName = (hostName: string) => {
  const labelCount = secondLevelPublicSuffixPattern.test(hostName) ? 3 : 2
  return hostName.split('.').slice(-labelCount).join('.')
}

/**
 * Check if the string is a valid URL
 * @param url URL you want to validate.
 * @returns a boolean if the url is valid or not
 */
export const isValidURL = (url: string) => {
  try {
    return Boolean(new URL(url))
  } catch {
    return false
  }
}

/**
 * Converts the passed object to the URL query string
 * nulls and undefined values are skipped
 * @param query - Query params as an object.
 * @returns a URL query string
 */
export const stringifyUrlObject = <T extends object>(query: T) =>
  stringify(query, {
    arrayFormat: 'repeat',
    encodeValuesOnly: true,
    skipNulls: true
  })

/**
 * Builds a URL with the given query parameters.
 * Null and undefined values are skipped.
 * @param url - The base URL.
 * @param search - Query parameters as an object.
 * @param qsOptions - Options for stringifying the query parameters.
 * @returns The formatted URL with the query string.
 */
export const buildUrl = (
  url: string,
  search: Record<string, unknown> = {},
  qsOptions: qs.IStringifyOptions = {}
): string => {
  if (isNil(search)) {
    return url
  }

  const query = qs.stringify(search, {
    arrayFormat: 'repeat',
    encodeValuesOnly: true,
    ...qsOptions
  })

  return `${url}?${query}`
}

/**
 * Decode a URL encoded value without crashing
 * @param value - The value to decode
 * @returns - decoded value or empty string if the value is not valid
 */
export const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch (error) {
    return ''
  }
}

/**
 * Get the `label` URL parameter decoded into an object of nested key/value
 * pairs. Useful for extracting fields encoded inside the affiliate label
 * (e.g. `gsite`, `vf`, `wt`).
 * @param urlParams - a qs-parsed URL params object
 * @returns parsed object or an empty object if no label or anything fails
 */
export const getParsedLabel = (urlParams: Record<string, string>) =>
  urlParams.label
    ? (qs.parse(safeDecode(urlParams.label)) as Record<string, string>)
    : {}
